// The player card: opens from any player name as a DetailSheet.
//
// IN A REAL LEAGUE THE CARD READS ITSELF (W8): `GET …/players/:playerId` says
// who holds the player here, what I can do about it — or why nothing — and his
// season so far scored this league's way, in one read. The opener hands over
// what it already knows (the name, the game, this week's points) so the card
// has something to show before that answers. The action on it is the
// server's, and it opens the add, claim or drop sheet from the card itself, so
// every screen that opens a card — the lineup, the matchup, the pool — gets
// the same actions without wiring its own sheet. In the practice league the
// opener still says what the card offers, as it always has.
//
// Drawn through the kit (LOOKS.md Part 7).

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  COMPARE_MAX,
  failureOf,
  valueOf,
  type Availability,
  type DraftSeasonLine,
  type GameStatus,
  type PlayerCardView,
  type PlayerSummary,
  type PlayerWeekRow,
  type ProjectionBasis,
} from '@fsp/domain';
import {
  useLeagueBase,
  useLeagueZoneState,
  usePlayerCard,
  usePlayerNews,
  useReaderZone,
} from '../../data/hooks';
import { GameLabel, positionLabel, PositionPill, ProTeamCode, StatusBadge } from '../../kit';
import { Button, Caption, Chip, IconButton, Line, PinnedTop, Stack, Text } from '../../kit/ui';
import { whenLabel } from '../NewsScreen';
import '../../styles/screens/news.css';
import { DetailSheet } from '../../templates/DetailSheet';
import { num, pct } from './notComputed';
import { projectionNote } from './Projection';
import { SeasonLog } from './SeasonLog';
import { ComingUp } from './ComingUp';
import { WatchStar } from './WatchStar';
import { DeeperSection } from './Deeper';
import { useAtLeast } from '../../data/detail';
import { rankText, seasonRankWords } from './rank';
import { TeamName } from './Names';
import { DataCredit } from './DataCredit';
import { useLeagueSource } from '../../data/leagueSource';
import { useCompareTrayIfAny } from '../../shell/compareTray';
import { useChatDrawerIfAny } from '../../shell/chatDrawer';
import { playerLabel } from '../../shell/ShareTray';
import { fmtWhen, RosterMoveSheet, type RosterMove } from './RosterMoveSheet';
import { useTools } from '../../data/tools';
import { injuryWords } from './injuryWords';

/**
 * Just enough to open a card from a name (B-220): a screen that shows a
 * player's name in a sentence — the news, a trade, an award — has his id and
 * his name and nothing else. The card's own read brings the rest.
 */
export interface PlayerRef {
  id: string;
  name: string;
}

function isSummary(p: PlayerSummary | PlayerRef): p is PlayerSummary {
  return 'positions' in p;
}

/**
 * The card opened from inside a draft (B-337): where he went in it, or what
 * the board says of him while he is there to take, and the room's own moves.
 * Given, it stands where the season's facts and moves stand, since none of
 * them is true in a draft.
 */
export interface DraftCardFacts {
  /** Where he went: the pick, and who made it ("you", or the seat's drafter). `kept` is a kept mock's. */
  /** `price` is an auction's: he was bought, not picked (AUCTION.md). */
  taken: { pick: string; by: string; kept: boolean; price?: number } | null;
  /** The board's facts, while he is there to take. */
  row: {
    rank: number;
    positionRank: number | null;
    tier: number;
    bye: number | null;
    last: DraftSeasonLine | null;
  } | null;
  /** Draft him now: mine to make, or a commissioner's for the team on the clock. */
  pick: { label: string; onClick: () => void } | null;
  queue: { queued: boolean; onClick: () => void } | null;
}

/** Where he stands in a draft, in a sentence. */
export function draftWords(draft: DraftCardFacts): string {
  if (draft.taken !== null)
    return draft.taken.price !== undefined
      ? `Bought by ${draft.taken.by} for $${String(draft.taken.price)}${draft.taken.kept ? ' in this mock' : ''}.`
      : `Taken at ${draft.taken.pick} by ${draft.taken.by}${draft.taken.kept ? ' in this mock' : ''}.`;
  return draft.row !== null ? 'Still on the board.' : 'Not in this draft’s pool.';
}

export interface PlayerCardData {
  /** The whole summary from a row that has it; a name alone opens a card too (B-220). */
  player: PlayerSummary | PlayerRef;
  game: GameStatus | null;
  /** Null means nobody has projected this player — see `notComputed`. */
  projection: number | null;
  /** Where the projection came from, when the screen that opened the card has it. */
  basis?: ProjectionBasis | null;
  points: number | null;
  statLine: string | null;
  /** The practice league's action. A real league's card asks the server what it offers. */
  action?: { label: string; onClick: () => void; disabled?: boolean } | null;
  /**
   * Moving him within your own lineup, from My Team (B-288): the same move his
   * badge starts. The card's main button on your own player, where Drop is
   * never the main one (B-204).
   */
  move?: { label: string; onClick: () => void } | null;
  /** Opened from a draft: its facts and moves in place of the season's (B-337). */
  draft?: DraftCardFacts | null;
}

/**
 * Who holds the player in this league, in a sentence — his team's name one
 * tap from its roster (B-220). `onGo` closes the card that says it.
 */
function InLeague({ at, zone, onGo }: { at: Availability; zone: string; onGo: () => void }) {
  if (at.kind !== 'owned') return <>{inLeagueLine(at, zone)}</>;
  return (
    <>
      On <TeamName team={at.team} look="inline" onGo={onGo} /> · {at.team.ownerName}.
    </>
  );
}

/** Who holds the player in this league, in a sentence. */
export function inLeagueLine(at: Availability, zone: string): string {
  switch (at.kind) {
    case 'mine':
      return 'On your team.';
    case 'owned':
      return `On ${at.team.name} · ${at.team.ownerName}.`;
    case 'free-agent':
      return 'A free agent.';
    case 'waivers':
      return `On waivers until ${fmtWhen(at.clearsAt, zone)}.${at.myClaimId ? ' You have a claim on him.' : ''}`;
  }
}

/** The card's button for what the server says I can do, or null for nothing. */
function actionLabel(view: PlayerCardView): string | null {
  switch (view.action) {
    case 'add':
      return 'Add';
    case 'claim':
      return view.inLeague.kind === 'waivers' && view.inLeague.myClaimId ? 'Edit claim' : 'Claim';
    case 'drop':
      return 'Drop';
    case 'trade':
      return 'Trade for';
    case 'none':
      return null;
  }
}

/**
 * This week's points from the card's own read (SCREENS-3): his points once
 * his line is in, 0 once his game's lines are in without him — what the
 * matchup counts him, and what My Team's row shows (B-172) — and nothing
 * while it can still come.
 */
/**
 * How much of the site rosters him (B-414), the Players row's figures in
 * words: the share of the site's leagues that hold him, and the week's net
 * adds when there were any.
 */
export function rosteredWords(card: Pick<PlayerCardView, 'ownershipPct' | 'adds'>): string {
  const share = `Rostered in ${pct(card.ownershipPct)} of the site’s leagues`;
  if (card.adds === null || card.adds === 0) return `${share}.`;
  const n = Math.abs(card.adds);
  return `${share} · ${card.adds > 0 ? '+' : '−'}${n} net ${n === 1 ? 'add' : 'adds'} in the last 7 days.`;
}

export function weekPoints(week: PlayerWeekRow | null): number | null {
  if (week === null) return null;
  if (week.state === 'played') return week.points;
  return week.state === 'no-line' ? 0 : null;
}

/**
 * What this week says when there is no line to show, by the server's state:
 * "No stats yet" only while a line can still come, "No stats" once his game's
 * lines are in and he has none. The card said "No stats yet." beside a week
 * the season log below it scored, and beside a 0 whose lines were in
 * (round 3's review, SCREENS-3).
 */
export function weekWords(week: PlayerWeekRow | null): string {
  switch (week?.state ?? 'upcoming') {
    case 'bye':
      return 'No game this week.';
    case 'no-line':
      return 'No stats.';
    case 'pending':
      return 'No stats yet.';
    default:
      return 'Nothing yet. Kickoff is coming.';
  }
}

export function PlayerCard({
  data,
  onClose,
}: {
  data: PlayerCardData | null;
  onClose: () => void;
}) {
  const tools = useTools();
  const { leagueId } = useParams();
  const real = useLeagueSource() === 'api' && leagueId !== undefined;
  // The sheet a card's action opens. It outlives the card: choosing Add
  // closes the card and opens the sheet in its place.
  const [move, setMove] = useState<RosterMove | null>(null);
  // THE SHEET BELONGS TO THE PAGE IT WAS OPENED ON. The league's shared card
  // (B-220) stays mounted from screen to screen, so a Back or a tab left its
  // add or drop sheet open over the next one (the review of 2026-09-27).
  const { pathname } = useLocation();
  useEffect(() => setMove(null), [pathname]);
  return (
    <>
      <DetailSheet opened={data !== null} onClose={onClose} title={data ? 'Player' : undefined}>
        {data ? <PlayerCardBody data={data} onClose={onClose} onMove={setMove} /> : null}
      </DetailSheet>
      {/* The card is not leveled and shows his projection at every level, so
          the sheet it opens does too (B-177). */}
      {real ? (
        <RosterMoveSheet
          move={move}
          showProjection={tools.projections}
          onClose={() => setMove(null)}
        />
      ) : null}
    </>
  );
}

/**
 * THE CARD ITSELF, wherever it is drawn: in its sheet, or in My Team's side
 * panel beside the lineup from a tablet up (DIRECTION U21, B-316), where a
 * sheet would cover the roster the card is being read against. `onClose`
 * puts it away; `onMove` opens the add, claim or drop sheet its action
 * starts, which the host keeps; `onTrade`, where the host has the trade
 * builder at hand, opens it there rather than on the Trades screen.
 */
export function PlayerCardBody({
  data,
  onClose,
  onMove,
  onTrade,
}: {
  data: PlayerCardData;
  onClose: () => void;
  onMove: (move: RosterMove) => void;
  onTrade?: (teamId: string, playerId: string) => void;
}) {
  const tools = useTools();
  const newsRead = usePlayerNews(data.player.id);
  const news = newsRead.items;
  const clock = useLeagueZoneState();
  const zone = useReaderZone();
  const { leagueId } = useParams();
  const base = useLeagueBase();
  const navigate = useNavigate();
  const real = useLeagueSource() === 'api' && leagueId !== undefined;
  // The deeper numbers are the Everything level's (E8, DETAIL_LEVELS.md).
  const everything = useAtLeast('pro');
  // A rank is a number, so it is Standard's (B-321).
  const standard = useAtLeast('standard');
  const read = usePlayerCard(real ? data.player.id : null);
  const card = read === null ? null : valueOf(read);
  const cardFailure = read === null ? null : failureOf(read);
  const { pathname } = useLocation();
  // OPENED FROM A DRAFT, the draft's facts and moves stand where the season's
  // stand, Compare among them: it leaves the page, and leaving the room
  // mid-draft costs a drafter the room (B-337).
  const draft = data.draft ?? null;
  // COMPARE (E3, B-375): **Add to compare** puts him in the tray at the foot
  // of the screen, beside whoever is there, and the card stays open; pressed
  // again it takes him out. It started a new comparison each time, so a
  // second and third player had to be searched for one at a time. On
  // Compare itself the card is opened from a column he is already in.
  const comparing = pathname.endsWith('/compare');
  const tray = useCompareTrayIfAny();
  const chat = useChatDrawerIfAny();
  // ALL HIS NEWS (B-414): three, and the rest on a press, in place.
  const [allNews, setAllNews] = useState(false);

  const act = (view: PlayerCardView) => {
    const at = view.inLeague;
    if (view.action === 'trade') {
      // The builder with his owner and him filled in (TRADES.md "Trade for").
      // The card closes first: opened on Trades, the address keeps its path
      // and nothing else would close it (the review of 2026-09-27).
      if (at.kind === 'owned') {
        onClose();
        if (onTrade) onTrade(at.team.id, view.player.id);
        else
          navigate(
            `${base}/trades?new=1&with=${at.team.id}&get=${encodeURIComponent(view.player.id)}`,
          );
      }
      return;
    }
    if (view.action === 'none') return;
    onClose();
    onMove({
      kind: view.action,
      playerId: view.player.id,
      claimId: at.kind === 'waivers' ? at.myClaimId : null,
    });
  };

  // WHO HE IS: the card's own read once it answers, the opener's until then
  // — and from a name alone (B-220), nothing but the name until it does.
  const who: PlayerSummary | null = card?.player ?? (isSummary(data.player) ? data.player : null);
  const game = card?.game ?? data.game ?? null;
  const label = card === null ? null : actionLabel(card);
  // THIS WEEK: the card's own read once it answers, the opener's until then —
  // and in a real league, nothing said while it is still reading rather than
  // a guess from an opener that may not have known (Players and Waivers pass
  // no points at all).
  const week = card?.thisWeek ?? null;
  const points = card !== null ? weekPoints(week) : data.points;
  const statLine = card !== null ? (week?.statLine ?? null) : data.statLine;
  const emptyWords =
    statLine !== null
      ? null
      : card !== null
        ? weekWords(week)
        : real
          ? null
          : game?.state === 'pre'
            ? 'Nothing yet. Kickoff is coming.'
            : 'No stats yet.';

  return (
    <Stack gap={4}>
      {/* WHO HE IS AND HIS TWO NUMBERS STAY AT THE TOP while his news, his
          weeks ahead and his game log scroll under them (B-486): his name
          left with the first scroll, and the log was read against nobody. */}
      <PinnedTop className="fsp-card-top">
        <Stack gap={3}>
          <Line gap={3} align="start">
            {who?.positions[0] ? <PositionPill pos={who.positions[0]} /> : null}
            <div className="fsp-card-who">
              <div className="fsp-card-name-line">
                <div className="fsp-disp fsp-card-name">{who?.name ?? data.player.name}</div>
                {/* The watchlist's star (E5): the card's own answer once read. */}
                {real ? <WatchStar player={data.player} fallback={card?.watched ?? false} /> : null}
                {/* SHARE IN CHAT (PLAN-2026-10-31-players-and-games-in-chat
                    §1): him, attached to the next line in the chat that opens;
                    nothing is sent until the manager sends it. */}
                {real && chat?.canShare && who !== null ? (
                  <IconButton
                    icon="share"
                    label="Share in chat"
                    variant="quiet"
                    size="sm"
                    onClick={() => {
                      chat.attach({
                        share: { kind: 'player', playerId: who.id },
                        label: playerLabel(who),
                      });
                      onClose();
                    }}
                  />
                ) : null}
              </div>
              {who !== null ? (
                <Line gap={1}>
                  <ProTeamCode code={who.proTeam} />
                  <StatusBadge player={who} />
                  <GameLabel game={game} />
                </Line>
              ) : null}
            </div>
          </Line>
          {who?.injury ? (
            <Text tone="secondary" block>
              {injuryWords(who.injury, zone)}
            </Text>
          ) : null}
          {draft !== null ? (
            <Line gap={2} align="start" wrap>
              <span className="fsp-card-held">
                <Text>{draftWords(draft)}</Text>
              </span>
              <Line gap={2}>
                {draft.pick ? (
                  <Button variant="primary" size="xs" onClick={draft.pick.onClick}>
                    {draft.pick.label}
                  </Button>
                ) : null}
                {draft.queue ? (
                  <Button size="xs" onClick={draft.queue.onClick}>
                    {draft.queue.queued ? 'Take off my queue' : 'Add to my queue'}
                  </Button>
                ) : null}
              </Line>
            </Line>
          ) : real ? (
            <Line gap={2} align="start" wrap>
              <span className="fsp-card-held">
                <Text>
                  {card !== null && zone !== null ? (
                    <InLeague at={card.inLeague} zone={zone} onGo={onClose} />
                  ) : (
                    (cardFailure?.message ?? clock.failure ?? 'Reading the league…')
                  )}
                </Text>
              </span>
              {/* THE ACTION BESIDE WHO HOLDS HIM (B-219): with his schedule
                    and game log below, a button at the foot of the card was a
                    scroll away. Drop is not here — it stays the card's quiet
                    last line (B-204). In a narrow card the buttons go
                    under it together. */}
              <Line gap={2}>
                {data.move ? (
                  <Button variant="primary" size="xs" icon="move" onClick={data.move.onClick}>
                    {data.move.label}
                  </Button>
                ) : null}
                {card !== null && label !== null && card.action !== 'drop' ? (
                  <Button variant="primary" size="xs" onClick={() => act(card)}>
                    {label}
                  </Button>
                ) : null}
                {card !== null && !comparing && tray !== null && tray.leagueId !== null ? (
                  tray.has(card.player.id) ? (
                    <Button
                      size="xs"
                      icon="accept"
                      label={`In compare: take ${card.player.name} out`}
                      onClick={() => tray.remove(card.player.id)}
                    >
                      In compare
                    </Button>
                  ) : (
                    <Button
                      size="xs"
                      icon="compare"
                      disabled={tray.full}
                      onClick={() => tray.toggle({ id: card.player.id, name: card.player.name })}
                    >
                      {tray.full ? `Compare holds ${COMPARE_MAX}` : 'Add to compare'}
                    </Button>
                  )
                ) : null}
                {/* A clock that could not be read is asked again by itself;
                    this is for the person who would rather not wait. */}
                {cardFailure === null && zone === null && clock.failure !== null ? (
                  <Button size="xs" onClick={clock.retry}>
                    Try again
                  </Button>
                ) : null}
              </Line>
            </Line>
          ) : null}
          {draft !== null ? (
            draft.row === null ? null : (
              // THE BOARD'S FACTS, as the room's list shows them: the league's
              // own rank, his last season scored this league's way.
              <div className="fsp-card-stats">
                <Stat label="Board rank" value={String(draft.row.rank)} />
                <Stat
                  label={draft.row.last ? `${String(draft.row.last.season)} a game` : 'Last season'}
                  value={draft.row.last ? draft.row.last.perGame.toFixed(1) : '—'}
                />
              </div>
            )
          ) : (
            <div className="fsp-card-stats">
              {/* A BYE SAYS BYE (B-515), as the Players row does, where 0.0 read as a bad week. */}
              {/* Not in a league that keeps no projections (RULES_SCHEMA §15c). */}
              {tools.projections ? (
                <Stat
                  label="Projected"
                  value={who?.onBye ? 'BYE' : num(card?.projection ?? data.projection)}
                />
              ) : null}
              <Stat label="This week" value={points === null ? '—' : points.toFixed(1)} />
            </div>
          )}
        </Stack>
      </PinnedTop>
      {draft !== null && draft.row !== null ? (
        <Text tone="secondary" block>
          {[
            who?.positions[0] && draft.row.positionRank !== null
              ? `${positionLabel(who.positions[0])}${String(draft.row.positionRank)}`
              : null,
            `tier ${String(draft.row.tier)}`,
            draft.row.bye === null ? null : `bye week ${String(draft.row.bye)}`,
          ]
            .filter((x) => x !== null)
            .join(' · ')}
          {draft.row.last
            ? `. ${String(draft.row.last.points.toFixed(1))} points in ${String(draft.row.last.games)} games in ${String(draft.row.last.season)}, this league’s scoring.`
            : '. No games last season.'}
        </Text>
      ) : null}
      {/* WHERE HE STANDS (B-321): his rank at his position by season points in
          this league's scoring, as the big sites rank, under the two numbers a
          card is opened for. */}
      {draft === null && standard && card !== null && card.seasonRank !== null && who !== null ? (
        <Text tone="secondary" block>
          <strong className="fsp-card-rank">{rankText(who.positions[0]!, card.seasonRank)}</strong>
          {' · '}
          {seasonRankWords(who.positions[0]!, card.seasonRank)}
        </Text>
      ) : null}
      {/* ROSTERED ACROSS THE SITE (B-414): the Players row's own figures,
          from Standard as the row's Own column is; nothing while no league
          has measured it. */}
      {draft === null && standard && card !== null && card.ownershipPct !== null ? (
        <Text tone="secondary" block>
          {rosteredWords(card)}
        </Text>
      ) : null}
      {/* Why there is no number, or why it is a stated 0 — the card's
                own read first, the row's otherwise. Words, at every level. */}
      {draft === null &&
      projectionNote(card?.projection ?? data.projection, card?.basis ?? data.basis ?? null) !==
        null ? (
        <Text tone="secondary" block>
          {projectionNote(card?.projection ?? data.projection, card?.basis ?? data.basis ?? null)}
        </Text>
      ) : null}
      {draft !== null ? null : statLine ? (
        <Text block>{statLine}</Text>
      ) : emptyWords !== null ? (
        <Text tone="secondary" block>
          {emptyWords}
        </Text>
      ) : null}
      <Stack gap={1}>
        <Caption>News</Caption>
        {news === null ? (
          // A real league's news on its way, or not readable: nothing
          // is said about it either way until it is read.
          <Text tone="secondary" block>
            Reading his news…
          </Text>
        ) : news.length === 0 ? (
          <Text tone="secondary" block>
            Nothing this week. No news is good news.
          </Text>
        ) : (
          <div className="fsp-surface">
            {(allNews ? news : news.slice(0, 3)).map((n) => (
              <div key={n.id} className="fsp-row fsp-card-news">
                <span className="fsp-news-dot" data-severity={n.severity} aria-hidden />
                <div className="fsp-card-news-body">
                  <div className="fsp-card-news-line">
                    {n.headline}
                    {n.body ? <span className="fsp-sub"> {n.body}</span> : null}
                  </div>
                  <div className="fsp-sub">
                    {zone === null ? n.source : `${whenLabel(n.publishedAt, zone)} · ${n.source}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {news !== null && (news.length > 3 || newsRead.older !== null) ? (
          <Line end gap={2}>
            {/* ALL OF IT (B-414): the rest of this page in place, then each
                page after it on asking, Older as News has it. */}
            {allNews && newsRead.older !== null ? (
              <Button size="xs" variant="quiet" onClick={newsRead.older}>
                Older
              </Button>
            ) : null}
            <Button size="xs" variant="quiet" onClick={() => setAllNews(!allNews)}>
              {allNews ? 'Fewer' : 'All his news'}
            </Button>
          </Line>
        ) : null}
        {/* HIS STATUS ELSEWHERE (B-482): our news is the league office's
            injury report, which lags game time, so a page that keeps it
            current is a link away — said to be the other site's, in a tab of
            its own, never framed or copied here. */}
        {real && card !== null
          ? card.elsewhere.map((page) => (
              <Line key={page.url} gap={2} wrap>
                <Text tone="secondary" size="sm">
                  {page.site} keeps his status current through game day, on their own site. It opens
                  in a new tab.
                </Text>
                <Button size="xs" variant="secondary" iconRight="forward" href={page.url}>
                  {`Go to ${page.site}`}
                </Button>
              </Line>
            ))
          : null}
      </Stack>
      {real && card !== null && card.deeper !== null && everything ? (
        <DeeperSection deeper={card.deeper} />
      ) : null}
      {real && card !== null && card.seasonSoFar !== null ? (
        <ComingUp
          season={card.seasonSoFar}
          position={who?.positions[0] ?? null}
          holder={card.inLeague.kind === 'owned' ? card.inLeague.team : null}
          onGo={onClose}
        />
      ) : null}
      {real ? (
        card === null ? null : (
          <SeasonLog season={card.seasonSoFar} last={card.lastSeason} />
        )
      ) : (
        <Chip>Season log and ownership arrive with the API</Chip>
      )}
      {draft !== null ? null : real ? (
        card === null ? null : label !== null ? (
          card.action === 'drop' ? (
            // DROP IS NEVER THE CARD'S MAIN BUTTON (B-204). On your own
            // player it was the one big button, so a card opened for
            // any reason — to read his injury, to pick him for a slot —
            // offered letting him go as the next thing to do. A quiet
            // red one, and the sheet it opens still asks and says what
            // happens to him.
            <Line end>
              <Button variant="danger-quiet" size="xs" onClick={() => act(card)}>
                Drop {who?.shortName ?? data.player.name}…
              </Button>
            </Line>
          ) : null
        ) : card.reason ? (
          <Text tone="secondary" block center>
            {card.reason}
          </Text>
        ) : null
      ) : data.move ? (
        // The tour's card: the move, as a real league's offers it.
        <Button variant="primary" icon="move" onClick={data.move.onClick} full>
          {data.move.label}
        </Button>
      ) : data.action ? (
        <Button
          variant="primary"
          onClick={data.action.onClick}
          disabled={data.action.disabled}
          full
        >
          {data.action.label}
        </Button>
      ) : null}
      {/* Where his numbers and news come from (B-593), the card's last line:
          a real league's, not the practice league's invented players. */}
      {real ? <DataCredit /> : null}
    </Stack>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="fsp-surface fsp-card-stat">
      <Caption>{label}</Caption>
      <div className="fsp-num fsp-disp fsp-card-stat-value">{value}</div>
    </div>
  );
}
